import { useCallback, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import useLoading from '../../hooks/useLoading';
import useInitializing from '../../hooks/useInitializing';
import useUncaughtErrors from '../../hooks/useUncaughtErrors';
import ErrorContainer from '../ErrorContainer';
import LoadingContainer from '../LoadingContainer';
import StatsAndStatus from 'src/HomePage/components/StatsAndStatus';
import useInterviewType from 'src/interviews/hooks/useInterviewType';

import type { SyntheticEvent } from 'react';
import type { IAccordionEmbeddedLayoutProps } from './types';

const AccordionEmbeddedLayout = ({
  children,
  defaultExpanded,
  onExpanded,
  title,
  triggerRestart,
}: IAccordionEmbeddedLayoutProps) => {
  const theme = useTheme();
  const errors = useUncaughtErrors();
  const isLoading = useLoading();
  const isInitializing = useInitializing();
  const showLoader = isLoading || isInitializing;
  const interviewType = useInterviewType();

  const handleChange = useCallback(
    (event: SyntheticEvent, expanded: boolean) => {
      if (expanded) {
        onExpanded?.(event);
      }
    },
    [onExpanded],
  );

  const Content = useMemo(() => {
    if (errors.length) {
      return <ErrorContainer triggerRestart={triggerRestart} />;
    }
    if (showLoader) {
      return <LoadingContainer />;
    }
    return children;
  }, [children, errors.length, showLoader, triggerRestart]);

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      elevation={0}
      onChange={handleChange}
      sx={{
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.teal[10]}`,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon color="primary" fontSize="large" />}
        sx={{
          '&.Mui-expanded p': {
            fontWeight: 'bolder',
          },
          backgroundColor: theme.palette.teal[10],
        }}
      >
        <div className="bp-flex bp-w-full bp-items-center bp-justify-between bp-font-primary bp-normal-case bp-text-gray-100">
          <span className="bp-flex bp-flex-col bp-justify-start bp-pr-6 bp-align-top bp-text-gray-100">
            <span className="bp-text-lg bp-leading-tight">{title}</span>
            <StatsAndStatus interviewType={interviewType} />
          </span>
        </div>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: '24px',
        }}
      >
        <Grid container direction="column" spacing={2.5}>
          {Content}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default AccordionEmbeddedLayout;
